import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { jsonAuthHeaders } from "@/utils/headers";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { displayToast, displayErrorToast } from "@/utils/toast";
import { ToastType } from "@/enums";
import { useAuth } from "@/context/auth-context";
import { DataRetentionPeriod } from "@/types/workspaces";

interface UpdateWorkspaceParams {
  workspaceId: number;
  name?: string;
  is_default?: boolean;
  data_retention_period?: DataRetentionPeriod;
}

export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();
  const auth = useAuth();

  return useMutation({
    mutationFn: ({ workspaceId, ...data }: UpdateWorkspaceParams) =>
      fetch(`${ENDPOINTS.workspaces}/${workspaceId}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(auth?.userToken ?? ""),
        body: JSON.stringify(data),
      }).then(handleResponse),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ENDPOINTS.workspaces] });
      queryClient.invalidateQueries({ queryKey: [ENDPOINTS.legacy_workspaces] });
      queryClient.invalidateQueries({ queryKey: [ENDPOINTS.organizations] });
      queryClient.invalidateQueries({ queryKey: [ENDPOINTS.me] });

      displayToast("Workspace updated successfully", ToastType.success);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      displayErrorToast(error?.message || "Failed to update workspace. Please try again.");
    },
  });
};
